import React, { useMemo, useState, useEffect } from 'react'
import { useLocation, Link as RouterLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'

import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import PersonIcon from '@mui/icons-material/Person'
import LogoutIcon from '@mui/icons-material/Logout'
import OnlineIcon from '@mui/icons-material/Link'

import logoImg from '@/assets/img/logo.png'
import colors from '@/assets/scss/_themes-vars.module.scss'

import { OfflineModeBadge } from '@/shared/offline/ui/OfflineStatusIndicator'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser, logout, setOnlineMode } from '@/redux/reducers/auth.reducer'
import { useOfflineMode } from '@/providers/OfflineModeProvider'

import { offlineSidebarItems } from '../offlineSidebarItems'
import { OfflineMobileSidebar } from '../OfflineMobileSidebar'

const HeaderIconButton = ({ children, tooltip, onClick }: { children: React.ReactNode; tooltip: string; onClick?: () => void }) => {
  const theme = useTheme()

  return (
    <Tooltip title={tooltip}>
      <IconButton
        onClick={onClick}
        sx={{
          width: '40px',
          height: '40px',
          background: 'transparent',
          border: `1px solid ${theme.palette.primary.light}`,
          color: theme.palette.background.default,
          '&:hover': {
            background: theme.palette.primary.light,
          },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  )
}

export const headerHeight = 56

export default function OfflineHeader() {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const theme = useTheme()
  const { t } = useTranslation()
  const authUser = useAppSelector(selectAuthUser)
  const { isOfflineMode, timeUntilExpiry } = useOfflineMode()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const navigate = useNavigate()

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const currentPageName = useMemo(() => {
    const matchedItem = offlineSidebarItems.find((menuItem) => (menuItem.isPathBase ? pathname === menuItem.link : pathname.startsWith(menuItem.link)))
    return matchedItem ? t(matchedItem.label) : t('Offline Mode')
  }, [pathname, t])

  const getUserInitials = () => {
    if (!authUser) return 'U'
    const name = authUser.userInfo.name || ''
    const surname = authUser.userInfo.surname || ''
    return `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase()
  }

  const formatTimeRemaining = (hours: number) => {
    const h = Math.floor(hours)
    const m = Math.floor((hours % 1) * 60)
    return `${h}h ${m}m`
  }

  const handleLogout = () => {
    dispatch(logout())
  }

  const handleGoOnline = () => {
    dispatch(setOnlineMode())
    navigate('/')
  }

  return (
    <AppBar position="sticky" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Container maxWidth={false}>
        <Toolbar disableGutters sx={{ minHeight: 'unset !important', height: `${headerHeight}px` }}>
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
            <Button component={RouterLink} to="/offline/patients">
              <Stack direction="row" alignItems="center" spacing={1}>
                <img alt="logo" src={authUser?.establishment?.logo?.url ?? logoImg} height="36px" />
                <CloudOffIcon sx={{ fontSize: 20, color: theme.palette.primary.contrastText }} />
              </Stack>
            </Button>
          </Box>

          {isMobileScreen && (
            <Stack>
              <Typography color="inherit">{currentPageName}</Typography>
            </Stack>
          )}

          <Stack direction="row" sx={{ flexGrow: 1 }}>
            {/* Mobile Sidebar */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <OfflineMobileSidebar />
            </Box>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ display: { xs: 'none', md: 'flex' }, color: (theme) => theme.palette.common.white }}
            >
              {offlineSidebarItems
                .filter((menuItem) => menuItem.isPathBase || pathname.startsWith(menuItem.link))
                .map((menuItem) => (
                  <Link
                    key={menuItem.name}
                    component={RouterLink}
                    underline="none"
                    sx={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    to={menuItem.link}
                  >
                    {t(menuItem.label)}
                  </Link>
                ))}
            </Breadcrumbs>

            <Stack direction="row" flex={1} alignItems="center" justifyContent="end" spacing={1}>
              {/* Offline Status Badge */}
              <OfflineModeBadge />

              {/* Time Remaining */}
              {timeUntilExpiry && timeUntilExpiry > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    color: colors.orange300,
                    fontWeight: 600,
                    border: `1px solid ${colors.orange300}`,
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {t('Expires in')} {formatTimeRemaining(timeUntilExpiry)}
                </Typography>
              )}

              {/* User Avatar */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: theme.palette.primary.light,
                    fontSize: '0.875rem',
                    border: `2px solid ${theme.palette.primary.contrastText}`,
                  }}
                >
                  {getUserInitials()}
                </Avatar>
                {!isMobileScreen && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'inherit', fontSize: '0.875rem' }}>
                      {authUser?.userInfo.name} {authUser?.userInfo.surname}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
                      {authUser?.email}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {isOnline && (
                <HeaderIconButton tooltip={t('Return to Online Mode')} onClick={handleGoOnline}>
                  <OnlineIcon fontSize="small" />
                </HeaderIconButton>
              )}

              {/* Logout Button */}
              <HeaderIconButton tooltip={t('Logout')} onClick={handleLogout}>
                <LogoutIcon fontSize="small" />
              </HeaderIconButton>
            </Stack>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  )
}
